// src/components/settings/WebPushSection.tsx
// Sezione "Notifiche push" + "Dispositivi connessi" usata nel SettingsModal.
//
// Pre-prompt UI: spieghiamo all'utente cosa attiva prima di chiamare
// Notification.requestPermission(), per evitare il "Block" istintivo.
import { useCallback, useEffect, useState } from 'react';
import { Bell, Loader2, Smartphone, Trash2 } from 'lucide-react';

import { Switch } from 'components/ui/switch';
import { Button } from 'components/ui/button';
import { useWebPush } from 'hooks/useWebPush';
import {
  listSubscriptions,
  deleteSubscriptionById,
  type BackendWebPushSubscription,
} from 'services/webpush';

function shortenUA(ua: string): string {
  if (!ua) return 'Dispositivo sconosciuto';
  // Best-effort: estrai browser + OS dall'UA. Non perfetto, ma sufficiente.
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' :
    'Browser';
  const os =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
    /Android/.test(ua) ? 'Android' :
    /Mac OS X/.test(ua) ? 'macOS' :
    /Windows/.test(ua) ? 'Windows' :
    /Linux/.test(ua) ? 'Linux' :
    'altro OS';
  return `${browser} su ${os}`;
}

export function WebPushSection() {
  const {
    isSupported,
    permission,
    isSubscribed,
    subscribe,
    unsubscribe,
  } = useWebPush();

  const [devices, setDevices] = useState<BackendWebPushSubscription[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoadingDevices(true);
    try {
      const list = await listSubscriptions();
      setDevices(list);
    } catch (err) {
      console.warn('[WebPushSection] listSubscriptions fallita:', err);
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, isSubscribed]);

  const handleToggle = async (next: boolean) => {
    setBusy(true);
    try {
      if (next) await subscribe();
      else await unsubscribe();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteDevice = async (id: number) => {
    setBusy(true);
    try {
      await deleteSubscriptionById(id);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  // Caso 1: API non supportata (iOS < 16.4, browser vecchi)
  if (!isSupported) {
    return (
      <div className="space-y-3">
        <h4 className="text-[var(--hexa-text-primary)] text-[11px] font-medium flex items-center gap-1.5">
          <Bell className="h-3 w-3" /> Notifiche push
        </h4>
        <div className="bg-[var(--hexa-surface-hover)] rounded-lg border border-[rgba(106,77,255,0.25)] p-3">
          <p className="text-[var(--hexa-text-secondary)] text-[10px] leading-relaxed">
            Il tuo browser non supporta le notifiche push.
            Su iPhone aggiungi Hexa alla schermata Home (iOS 16.4 o superiore).
          </p>
        </div>
      </div>
    );
  }

  // Caso 2: l'utente ha cliccato "Blocca" su quel sito
  const denied = permission === 'denied';

  return (
    <div className="space-y-3">
      <h4 className="text-[var(--hexa-text-primary)] text-[11px] font-medium flex items-center gap-1.5">
        <Bell className="h-3 w-3" /> Notifiche push
      </h4>

      <div className="bg-[var(--hexa-surface-hover)] rounded-lg border border-[rgba(106,77,255,0.25)] p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-[var(--hexa-text-primary)] text-[11px] font-medium">
              Ricevi notifiche sul telefono e desktop
            </p>
            <p className="text-[var(--hexa-text-secondary)] text-[10px] mt-1 leading-relaxed">
              Ti avvisiamo quando ti viene assegnato un task, hai un nuovo evento o
              quando arriva un'attività che ti riguarda — anche con l'app chiusa.
            </p>
          </div>
          <Switch
            checked={isSubscribed}
            disabled={busy || denied}
            onCheckedChange={handleToggle}
          />
        </div>

        {denied && (
          <p className="text-[var(--hexa-text-secondary)] text-[10px] italic">
            Le notifiche sono bloccate dalle impostazioni del browser.
            Sbloccale dall'icona del lucchetto in alto a sinistra e ricarica la pagina.
          </p>
        )}
      </div>

      {/* Dispositivi connessi */}
      <div className="space-y-2">
        <h5 className="text-[var(--hexa-text-secondary)] text-[10px] font-medium uppercase tracking-wider">
          Dispositivi connessi
        </h5>
        <div className="rounded-lg border border-[rgba(106,77,255,0.25)] divide-y divide-[rgba(106,77,255,0.15)] bg-[var(--hexa-surface)]">
          {loadingDevices && (
            <div className="p-3 flex items-center gap-2 text-[var(--hexa-text-secondary)] text-[10px]">
              <Loader2 className="h-3 w-3 animate-spin" /> Caricamento…
            </div>
          )}
          {!loadingDevices && devices.length === 0 && (
            <div className="p-3 text-[var(--hexa-text-secondary)] text-[10px]">
              Nessun dispositivo registrato. Attiva le notifiche per registrare questo.
            </div>
          )}
          {!loadingDevices && devices.map((d) => (
            <div key={d.id} className="p-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Smartphone className="h-3.5 w-3.5 text-[var(--hexa-text-secondary)] shrink-0" />
                <div className="min-w-0">
                  <p className="text-[var(--hexa-text-primary)] text-[10px] truncate">
                    {shortenUA(d.user_agent)}
                  </p>
                  <p className="text-[var(--hexa-text-secondary)] text-[9px]">
                    aggiornato {new Date(d.last_seen_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={busy}
                onClick={() => handleDeleteDevice(d.id)}
                aria-label="Disconnetti dispositivo"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
